import React from 'react';
import PublicNav from '../components/PublicNav';
import PublicFooter from '../components/PublicFooter';
import usePageMeta from '../utils/usePageMeta';

function Section({ title, children }) {
  return (
    <div className="mb-10">
      <h2 className="text-navy-900 font-semibold text-lg mb-3">{title}</h2>
      <div className="text-gray-600 text-sm leading-relaxed space-y-3">{children}</div>
    </div>
  );
}

function BulletList({ items }) {
  return (
    <ul className="list-disc pl-5 space-y-2">
      {items.map((item) => <li key={item}>{item}</li>)}
    </ul>
  );
}

export default function Privacy() {
  usePageMeta(
    'Privacy Policy',
    "How Tactmark collects, uses, protects, retains, and deletes organization data, match data, player data, and report-processing records."
  );

  return (
    <div className="min-h-screen bg-white">
      <PublicNav />
      <div className="max-w-3xl mx-auto px-6 py-16">
        <p className="text-navy-600 text-xs font-semibold tracking-widest uppercase mb-4">Legal</p>
        <h1 className="font-serif text-4xl text-navy-900 leading-tight mb-3">Privacy Policy</h1>
        <p className="text-gray-400 text-sm mb-12">Last updated: March 2025</p>

        <Section title="Overview">
          <p>
            This Privacy Policy explains how Tactmark collects, uses, stores, protects, and
            deletes information submitted to the platform. Tactmark is built for clubs,
            academies, federations, schools, analysis companies, and solo coaches who need a
            private workspace for match evidence, coaching observations, player or role reviews,
            and staff-ready reports.
          </p>
          <p>
            Organization data is isolated between customers. One organization cannot access the
            workspace, reports, players, match records, or uploaded references of another
            organization.
          </p>
        </Section>

        <Section title="Data controller and contact">
          <p>
            Tactmark is a US-based software company operating from the United States. For account,
            billing, and platform operation data, Tactmark acts as the data controller. For team,
            player, match, coaching, and organization records entered by a customer, the customer
            organization is responsible for deciding what data is submitted and who may access it
            inside that organization.
          </p>
          <p>
            Questions about privacy, data access, correction, export, or deletion can be sent to{' '}
            <a href="mailto:privacy@tactmark.com" className="text-navy-700 underline">privacy@tactmark.com</a>.
          </p>
        </Section>

        <Section title="Information we collect">
          <p>Tactmark may collect the following categories of information:</p>
          <BulletList items={[
            'Account information: name, email address, password credentials, role, login status, and account settings.',
            'Organization information: organization name, type, country, city, contact details, trial status, subscription status, and workspace settings.',
            'Team and player information: team names, player names, positions, numbers, roles, and staff-entered development notes.',
            'Match information: opponent, date, competition, venue, score, tactical focus, formations, key moments, timestamps, observations, and evidence notes.',
            'Report information: generated report sections, scoring outputs, training recommendations, action status, pattern history, and staff review notes.',
            'Report sharing information: read-only share tokens, expiry settings, and revocation status.',
            'Federation information: development objectives, programs, participating teams, reporting periods, templates, program submissions, organizational findings, assigned actions, leadership reviews, audit history, and exports.',
            'Support and communication information: messages, demo requests, organization type, number of teams, estimated users, development reporting process, operational needs, timing, procurement requirements, lead source, and delivery status.',
            'Billing information: plan, billing cycle, subscription and customer references, payment status, access dates, and invoice or receipt metadata handled with Stripe.',
            'Technical information: IP address, browser type, device information, authentication events, security logs, error logs, and usage records needed to operate and protect the service.'
          ]} />
        </Section>

        <Section title="Team, player, and match data">
          <p>
            Team names, player names, match records, key moments, coach observations, role
            reviews, and tactical notes are stored so your organization can create reports,
            compare matches over time, and identify recurring team patterns within its own
            workspace.
          </p>
          <p>
            Customers are responsible for ensuring they have the appropriate authority, consent,
            or lawful basis to submit player, staff, student, academy, or minor-related data into
            Tactmark. If your organization works with minors, you should only enter information
            that is necessary for legitimate coaching, development, safeguarding, or operational
            purposes and should follow your own internal policies and local laws.
          </p>
        </Section>

        <Section title="Uploaded footage and external references">
          <p>
            Tactmark may allow organizations to attach uploaded footage, file references, or
            external video links to match records. These references are used to support staff
            review and report context. In the current version of the product, attached footage is
            for reference and is not automatically analyzed as raw video.
          </p>
          <p>
            Private files are accessible only to authorized users in the relevant organization,
            using restricted access controls and time-limited links where applicable. External
            links remain subject to the privacy and access settings of the external service where
            the video is hosted.
          </p>
        </Section>

        <Section title="Report data and processing">
          <p>
            Reports are produced by the Tactmark Analysis Engine using structured match data,
            coach-entered observations, deterministic scoring, pattern comparison, and
            Tactmark-controlled report-writing workflows. Submitted match context, key moments,
            timestamps, role reviews, and notes may be processed by trusted service infrastructure
            strictly to produce your organization&apos;s report output, maintain the service, and
            protect the platform.
          </p>
          <p>
            Your organization&apos;s data is not used to train external systems, is not sold, and is
            not shared with other organizations on the platform.
          </p>
        </Section>

        <Section title="How we use information">
          <p>Tactmark uses information to:</p>
          <BulletList items={[
            'Create and operate organization workspaces.',
            'Authenticate users and protect accounts.',
            'Store team, player, match, evidence, and report records.',
            'Generate coaching reports, risk scores, training recommendations, and recurring-pattern summaries.',
            'Maintain dashboards, player development views, action trackers, and report history.',
            'Provide customer support and respond to requests.',
            'Process subscriptions, invoices, payment status, and account administration.',
            'Monitor service reliability, prevent abuse, investigate security issues, and enforce platform terms.',
            'Comply with legal obligations and respond to valid legal requests where required.'
          ]} />
        </Section>

        <Section title="Legal bases where applicable">
          <p>
            Where privacy laws such as GDPR or similar frameworks apply, Tactmark relies on one
            or more lawful bases depending on the activity:
          </p>
          <BulletList items={[
            'Contract necessity: to provide the Tactmark service, accounts, reports, workspaces, billing, and support.',
            'Legitimate interests: to secure the platform, improve reliability, prevent abuse, maintain audit logs, and support customer operations.',
            'Consent: where a customer or user chooses to provide optional information or where local law requires consent.',
            'Legal obligation: where Tactmark must retain or disclose limited information to comply with applicable law.'
          ]} />
        </Section>

        <Section title="Service providers and processors">
          <p>
            Tactmark uses trusted service providers to host the platform, store data, deliver
            emails, process payments, secure accounts, monitor reliability, and support report
            processing. These providers may process limited information only as needed to deliver
            their services to Tactmark and are not permitted to use customer data for their own
            independent purposes.
          </p>
          <p>
            Payment details are handled by the payment processor. Tactmark does not store full
            card numbers or full payment credentials on its own servers.
          </p>
        </Section>

        <Section title="Data sharing">
          <p>Tactmark does not sell customer data. We may share limited information only:</p>
          <BulletList items={[
            'With authorized users inside the same customer organization.',
            'With service providers that help operate, secure, bill, support, or process the platform.',
            'When required to comply with law, legal process, or enforceable government request.',
            'To protect the rights, safety, and security of Tactmark, customers, users, or the public.',
            'In connection with a business transfer, merger, acquisition, financing, or sale of assets, subject to appropriate confidentiality protections.'
          ]} />
        </Section>

        <Section title="International transfers">
          <p>
            Tactmark and its service providers may process information in countries other than the
            country where the customer or user is located. Where required, Tactmark relies on
            appropriate safeguards for international transfers, such as contractual protections,
            data-processing terms, or other lawful transfer mechanisms. Customers with
            cross-border or youth-program requirements should have their counsel review applicable
            transfer and retention obligations for their jurisdiction.
          </p>
        </Section>

        <Section title="Tactmark Federation">
          <p>
            When Tactmark Federation is enabled for an organization, authorized technical
            leadership and other entitled users may access objectives, program submissions,
            organizational findings, assigned actions, and review outcomes within that same
            organization. Optional integrations may add references from other authorized systems.
            Federation does not grant access to other customers&apos;
            workspaces.
          </p>
          <p>
            Youth, academy, and program data remain customer-controlled. The customer organization
            decides which programs and teams participate, which users may access Federation views,
            and what player or minor-related information is entered. Tactmark does not use
            Federation data to train external systems or share it across organizations.
          </p>
        </Section>

        <Section title="Cookies, sessions, and logs">
          <p>
            Tactmark uses essential cookies, session storage, authentication tokens, and technical
            logs to keep users signed in, maintain security, prevent abuse, debug errors, and
            operate the platform. If non-essential analytics or marketing cookies are introduced,
            this policy will be updated and any required notices or choices will be provided.
          </p>
        </Section>

        <Section title="Data retention">
          <p>
            Tactmark retains organization data for as long as the account is active or as needed
            to provide the service. Some records may be retained for a limited period after account
            closure for backup, security, fraud prevention, dispute resolution, accounting, or
            legal compliance purposes.
          </p>
          <p>
            Report history, match records, player records, uploaded references, and organization
            settings may be deleted or exported upon verified request, subject to technical limits,
            legal obligations, backup windows, and the customer&apos;s own responsibilities to preserve
            records where required.
          </p>
        </Section>

        <Section title="Deletion and account closure">
          <p>
            A verified organization owner may request deletion of the organization&apos;s workspace by
            contacting{' '}
            <a href="mailto:privacy@tactmark.com" className="text-navy-700 underline">privacy@tactmark.com</a>.
            Deletion requests will be reviewed and processed within a reasonable timeframe. Tactmark
            may ask for confirmation before deleting organization-level data because deletion may
            remove reports, match records, player history, uploaded references, and action-tracker
            records for all users in that organization.
          </p>
        </Section>

        <Section title="Security">
          <p>
            Tactmark uses administrative, technical, and organizational safeguards designed to
            protect customer data. These include authentication, password hashing, organization-level
            data scoping, restricted access controls, server-side handling of secrets, and security
            review of platform operations.
          </p>
          <p>
            No online service can guarantee absolute security. Customers should protect their
            login credentials, assign appropriate user roles, remove users who no longer need
            access, and avoid uploading unnecessary sensitive information.
          </p>
        </Section>

        <Section title="Security incidents">
          <p>
            If Tactmark becomes aware of a security incident that materially affects customer data,
            we will investigate and take appropriate steps to contain and remediate the issue. Where
            required by law, we will notify affected customers or regulators within applicable
            timeframes.
          </p>
        </Section>

        <Section title="Your rights">
          <p>
            Depending on your location, you may have rights to access, correct, export, restrict,
            object to processing, or delete personal data. Organization users should first contact
            their organization owner or administrator for workspace data requests. You may also
            contact Tactmark at{' '}
            <a href="mailto:privacy@tactmark.com" className="text-navy-700 underline">privacy@tactmark.com</a>.
          </p>
          <p>
            We may need to verify your identity or authority before acting on a request. Some
            requests may be limited where data is controlled by the customer organization, needed
            for security, required for legal compliance, or already anonymized or deleted.
          </p>
        </Section>

        <Section title="Customer responsibilities">
          <p>
            Customers are responsible for the accuracy and lawfulness of the data they enter into
            Tactmark, for managing authorized users, for obtaining any required permissions from
            staff, players, parents, guardians, schools, clubs, leagues, or governing bodies, and
            for avoiding unnecessary sensitive information in coach notes or uploaded references.
          </p>
        </Section>

        <Section title="Changes to this policy">
          <p>
            Tactmark may update this Privacy Policy from time to time. If material changes are
            made, the updated policy will be posted on this page with a revised date. Continued use
            of Tactmark after an update means the current policy applies to future use of the
            platform.
          </p>
        </Section>

        <Section title="Contact">
          <p>
            Tactmark operates from the United States. Questions about this policy, data requests,
            privacy concerns, or organization deletion requests can be directed to{' '}
            <a href="mailto:privacy@tactmark.com" className="text-navy-700 underline">privacy@tactmark.com</a>.
          </p>
        </Section>
      </div>
      <PublicFooter />
    </div>
  );
}
